import { useEffect, useState } from "react";
import { EntityAccessPermission, getEntityAccessPermissions } from "../../api/entityPermissions";
import { useErrorHandler } from "../Error/useErrorHandler";

export type EntityPermissionLevel = "read" | "write";

export const useEntityAccessPermissions = () => {
  const handleError = useErrorHandler();

  const [entityAccessMap, setEntityAccessMap] = useState<Map<number, EntityPermissionLevel>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  // Fetch the permission level for every entity the user has access to
  useEffect(() => {
    const fetchEntityAccess = async () => {
      setIsLoading(true);
      try {
        const data: EntityAccessPermission[] = await getEntityAccessPermissions();
        setEntityAccessMap(new Map(data.map((item) => [item.entityId, item.permissionType])));
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntityAccess();
  }, []);

  const getEntityPermission = (entityId: number): EntityPermissionLevel | undefined =>
    entityAccessMap.get(entityId);

  const canEditEntity = (entityId: number) => getEntityPermission(entityId) === "write";
  const canViewEntity = (entityId: number) => entityAccessMap.has(entityId);

  return {
    isLoading,
    entityAccessMap,
    canEditEntity,
    canViewEntity,
  };
};